import * as React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ErrorOutline,
  EditOutlined,
  SupportAgentOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import { getUserProfile, requestResubmission, ResubmitError } from "../api/profileApi";
import type { ResubmitResponse } from "../api/profileApi";
import type { UserProfile } from "../types/profile";
import EditProfileModal from "./EditProfileModal";
import { useAuth } from "../../../contexts/AuthContext";

// ─── 거절 사유 코드 매핑 ─────────────────────────────────────────────────────

interface RejectReasonInfo {
  label: string;
  guide: string;
}

const REJECT_REASON_MAP: Record<string, RejectReasonInfo> = {
  PHOTO_BLUR: {
    label: "프로필 사진이 흐리거나 얼굴이 잘 보이지 않아요",
    guide: "정면 사진, 밝은 곳에서 찍은 사진으로 교체해주세요",
  },
  PHOTO_MISSING: {
    label: "프로필 사진이 부족해요",
    guide: "사진을 5장 이상 등록해주세요",
  },
  PHOTO_INAPPROPRIATE: {
    label: "부적절한 사진이 포함되어 있어요",
    guide: "서비스 정책에 맞는 사진으로 교체해주세요",
  },
  INFO_INCOMPLETE: {
    label: "필수 정보가 부족해요",
    guide: "직업, 거주지, 자기소개를 모두 입력해주세요",
  },
  INFO_INCONSISTENT: {
    label: "입력하신 정보가 사실과 다를 수 있어요",
    guide: "정확한 정보로 수정해주세요",
  },
  AGE_OUT_OF_RANGE: {
    label: "가입 연령 기준에 맞지 않아요",
    guide: "Silver Connect는 50~70세 대상 서비스예요",
  },
  DUPLICATE: {
    label: "중복 계정이 확인되었어요",
    guide: "기존 계정으로 로그인해주세요",
  },
};

function getReasonInfo(code: string): RejectReasonInfo {
  return (
    REJECT_REASON_MAP[code] ?? {
      label: code,
      guide: "자세한 내용은 아래 메모를 확인해주세요",
    }
  );
}

// ─── BLOCKED 화면 ────────────────────────────────────────────────────────────

function BlockedView({ onLogout }: { onLogout: () => void }) {
  return (
    <Box sx={{ textAlign: "center" }}>
      <ErrorOutline sx={{ fontSize: 72, color: "error.main", mb: 2 }} />
      <Typography variant="h5" fontWeight={800} color="error.main" gutterBottom>
        재심사 기회가 소진되었습니다
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        3회의 재심사 기회를 모두 사용하셨습니다.
        <br />
        추가 문의는 고객센터로 연락해주세요.
      </Typography>
      <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
        <AlertTitle>안내사항</AlertTitle>
        더 이상 재심사 요청이 불가합니다. 고객센터를 통해 별도 안내를 받으실
        수 있습니다.
      </Alert>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<SupportAgentOutlined />}
          onClick={() => window.open("mailto:support@silverconnect.com")}
        >
          고객센터 문의
        </Button>
        <Button variant="contained" color="error" onClick={onLogout}>
          로그아웃
        </Button>
      </Box>
    </Box>
  );
}

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────

interface Props {
  onLogout: () => void;
}

export default function RejectedStatusPage({ onLogout }: Props) {
  const { updateUser } = useAuth();
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [resubmitResult, setResubmitResult] = React.useState<ResubmitResponse | null>(null);
  const [editModalOpen, setEditModalOpen] = React.useState(false);

  const loadProfile = React.useCallback(() => {
    setLoading(true);
    getUserProfile()
      .then(setProfile)
      .catch(() => setFetchError("프로필 정보를 불러올 수 없습니다."))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // 프로필 저장 완료 후 자동으로 재심사 요청
  const handleEditSave = React.useCallback(async () => {
    setEditModalOpen(false);
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await requestResubmission();
      setResubmitResult(result);
      setSubmitSuccess(true);
      // localStorage에도 반영되도록 AuthContext 업데이트
      updateUser({ status: 'PENDING' as any });
    } catch (err) {
      if (err instanceof ResubmitError) {
        const messages: Record<string, string> = {
          ALREADY_PENDING: "이미 심사가 진행 중입니다.",
          PROFILE_NOT_MODIFIED: "프로필 수정이 반영되지 않았습니다. 잠시 후 다시 시도해주세요.",
          MAX_RESUBMISSION_EXCEEDED: "재심사 기회가 소진되었습니다.",
          RESUBMIT_NOT_REJECTED_STATUS: "현재 상태에서는 재심사를 요청할 수 없습니다.",
        };
        setSubmitError(messages[err.code] ?? err.message);
      } else {
        setSubmitError("재심사 요청 중 오류가 발생했습니다. 다시 시도해주세요.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [updateUser]);

  // 콤마 구분 문자열 → 배열 파싱 (e.g. "PHOTO_BLUR,INFO_INCOMPLETE" → ["PHOTO_BLUR","INFO_INCOMPLETE"])
  const rejectCodes: string[] = React.useMemo(() => {
    if (!profile?.rejectReasonCodes) return [];
    return profile.rejectReasonCodes
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
  }, [profile?.rejectReasonCodes]);

  const isBlocked =
    profile != null &&
    (profile.remainingChances === 0 ||
      (profile.resubmissionCount != null && profile.resubmissionCount >= 3));

  const remainingChances = profile?.remainingChances ?? (3 - (profile?.resubmissionCount ?? 0));

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // 재심사 요청 성공 → PENDING 상태 안내
  if (submitSuccess) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <Container maxWidth="sm" sx={{ pt: 8 }}>
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
            <CheckCircleOutline sx={{ fontSize: 72, color: "success.main", mb: 2 }} />
            <Typography variant="h5" fontWeight={800} gutterBottom>
              재심사 요청이 완료되었습니다
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              관리자 검토 후 결과를 알려드립니다.
              {resubmitResult != null && (
                <>
                  <br />
                  남은 재심사 기회: <strong>{resubmitResult.remainingChances}회</strong>
                </>
              )}
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
              심사가 완료되면 알림으로 결과를 알려드립니다. 앱에 다시 접속하시면 최신 상태를 확인할 수 있어요.
            </Alert>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Button variant="contained" onClick={() => window.location.reload()} sx={{ borderRadius: 2, bgcolor: "#0D1F3C", "&:hover": { bgcolor: "#0A1830" } }}>
                홈으로 돌아가기
              </Button>
              <Button variant="text" onClick={onLogout} sx={{ color: "text.disabled", fontSize: "0.875rem" }}>
                로그아웃
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FA" }}>
      <Container maxWidth="sm" sx={{ pt: 6, pb: 6 }}>
        <Paper
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            boxShadow: "0 2px 16px rgba(13,31,60,0.10)",
          }}
        >
          {isBlocked ? (
            <BlockedView onLogout={onLogout} />
          ) : (
            <>
              {/* 헤더 */}
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography sx={{ fontSize: 52, mb: 1 }}>😥</Typography>
                <Typography variant="h5" fontWeight={800} color="#1A1A2E" gutterBottom>
                  심사가 반려되었습니다
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  아래 내용을 확인하고 프로필을 수정한 뒤 재심사를 요청해주세요.
                </Typography>
              </Box>

              {/* 거절 사유 */}
              {fetchError ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {fetchError}
                </Alert>
              ) : (
                <Box sx={{ mb: 3 }}>
                  {/* 표준 사유 코드 */}
                  {rejectCodes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {rejectCodes.map((code) => {
                        const info = getReasonInfo(code);
                        return (
                          <Alert
                            key={code}
                            severity="error"
                            icon={false}
                            sx={{ mb: 1.5, borderRadius: 2 }}
                          >
                            <AlertTitle sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                              반려 사유
                            </AlertTitle>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {info.label}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.5,
                                mt: 1,
                                p: "8px 12px",
                                bgcolor: "rgba(255,255,255,0.6)",
                                borderRadius: 1.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#333", fontWeight: 600, flexShrink: 0 }}
                              >
                                💡
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#333" }}>
                                {info.guide}
                              </Typography>
                            </Box>
                          </Alert>
                        );
                      })}
                    </Box>
                  )}

                  {/* 어드민 자유 메모 */}
                  {profile?.rejectReason && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      <AlertTitle sx={{ fontWeight: 700 }}>추가 안내</AlertTitle>
                      <Typography variant="body2">{profile.rejectReason}</Typography>
                    </Alert>
                  )}

                  {/* 코드도 메모도 없는 경우 */}
                  {rejectCodes.length === 0 && !profile?.rejectReason && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      <AlertTitle sx={{ fontWeight: 700 }}>반려 안내</AlertTitle>
                      <Typography variant="body2">
                        심사가 반려되었습니다. 프로필을 검토하고 수정 후 재심사를 요청해주세요.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {/* 남은 재심사 기회 */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 3,
                  p: "10px 14px",
                  bgcolor: "#F0F4FF",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  재심사 기회
                </Typography>
                <Chip
                  label={`3회 중 ${remainingChances}회 남음`}
                  size="small"
                  sx={{
                    bgcolor: remainingChances > 1 ? "#E8F5E9" : "#FFF3E0",
                    color: remainingChances > 1 ? "#2E7D32" : "#E65100",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                  }}
                />
              </Box>

              {/* 제출 오류 */}
              {submitError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {submitError}
                </Alert>
              )}

              {/* CTA 버튼 */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <EditOutlined />
                    )
                  }
                  disabled={submitting}
                  onClick={() => setConfirmOpen(true)}
                  sx={{
                    minHeight: 52,
                    borderRadius: 2,
                    bgcolor: "#0D1F3C",
                    fontWeight: 700,
                    fontSize: "1rem",
                    "&:hover": { bgcolor: "#0A1830" },
                  }}
                >
                  {submitting ? "재심사 요청 중..." : "수정하고 재심사 요청하기"}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  startIcon={<SupportAgentOutlined />}
                  onClick={() => window.open("mailto:support@silverconnect.com")}
                  sx={{ color: "text.secondary", fontSize: "0.875rem" }}
                >
                  고객센터 문의
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={onLogout}
                  sx={{ color: "text.disabled", fontSize: "0.8rem" }}
                >
                  로그아웃
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>

      {/* 프로필 수정 모달 */}
      {profile && (
        <EditProfileModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleEditSave}
          profile={profile}
          submitLabel="재심사 요청"
          submittingLabel="재심사 요청 중..."
        />
      )}

      {/* 재심사 확인 다이얼로그 */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>수정 후 재심사를 요청하시겠어요?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            프로필을 수정한 뒤 바로 재심사를 요청합니다.
            {remainingChances > 0 && (
              <>
                <br />
                <br />
                남은 재심사 기회: <strong>{remainingChances}회</strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            취소
          </Button>
          <Button
            onClick={() => { setConfirmOpen(false); setEditModalOpen(true); }}
            variant="contained"
            sx={{ borderRadius: 2, bgcolor: "#0D1F3C", "&:hover": { bgcolor: "#0A1830" } }}
          >
            프로필 수정하기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
